import { useEffect, useState } from 'react'
import { Box, IconButton, Link, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Link as RouterLink } from 'react-router-dom'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { useAuth } from '../context/AuthContext'
import settingsApi from '../client/settings-api'

const SETTINGS_CACHE_KEY = 'settings-cache'

const Bar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.brand.main + '1a',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& svg': {
    fontSize: 18,
    color: theme.palette.brand.main,
  },
}))

const BannerText = styled(Typography)({
  fontSize: 14,
})

const DismissButton = styled(IconButton)(({ theme }) => ({
  '& svg': {
    fontSize: 16,
    color: theme.palette.text.secondary,
  },
}))

// One-time "New here?" strip pointing signed-in users at /guide. Dismissal is
// stored in the account's settings (Settings_GuideBannerDismissed), so it
// stays dismissed across browsers and devices.
const GuideBanner = () => {
  const { isAuthenticated, getAccessToken } = useAuth()
  // null = unknown yet; the banner only renders once we know it's not dismissed.
  const [dismissed, setDismissed] = useState<boolean | null>(null)

  useEffect(() => {
    if (!isAuthenticated) {
      setDismissed(null)
      return
    }

    // The login-time sync (AccountThemeSync) keeps this cache fresh; fall back
    // to fetching when it isn't there yet.
    const cached = localStorage.getItem(SETTINGS_CACHE_KEY)
    if (cached) {
      try {
        setDismissed(JSON.parse(cached).guideBannerDismissed === true)
        return
      } catch {
        /* corrupt cache - fetch below */
      }
    }

    let cancelled = false
    ;(async () => {
      try {
        const token = await getAccessToken()
        const response = await settingsApi.get(token)
        if (!cancelled) {
          setDismissed(response.data.guideBannerDismissed === true)
        }
      } catch {
        /* can't tell - keep the banner hidden rather than nag wrongly */
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  if (!isAuthenticated || dismissed !== false) return null

  const dismiss = async () => {
    setDismissed(true)
    try {
      const token = await getAccessToken()
      const response = await settingsApi.update(token, {
        guideBannerDismissed: true,
      })
      localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(response.data))
    } catch {
      /* worst case it reappears next visit */
    }
  }

  return (
    <Bar>
      <AutoAwesomeIcon />
      <BannerText>
        New here?{' '}
        <Link component={RouterLink} to='/guide' underline='hover'>
          See everything you can do on Movieception
        </Link>
      </BannerText>
      <DismissButton size='small' onClick={dismiss} aria-label='Dismiss'>
        <CloseRoundedIcon />
      </DismissButton>
    </Bar>
  )
}

export default GuideBanner
